import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  model,
  output,
  Signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { DsBoxModule, DsModalModule } from '@bmw-ds/components';

import { useI18n } from '../../i18n/services/i18n.service';
import { modalOpenOrClosing } from '../../utils/signal/modalOpenOrClosing';
import { VerifyChoiceModalComponent } from './verify-choice-modal.component';

@Component({
  selector: 'cdf-verify-choice-modal-wrapper',
  imports: [DsModalModule, DsBoxModule, VerifyChoiceModalComponent],
  template: `
    <ds-modal
      [ds-modal-trigger-for]="modal"
      [(isModalOpen)]="open"
      [isDismissable]="!buttonLoading()"
      #modal
      (modalDismiss)="closeModal($event)"
      [size]="size()"
    >
      <ds-box-header> {{ headingText() }} </ds-box-header>
      <ds-box-content>
        @if (openOrClosing()) {
          <cdf-verify-choice-modal
            [bodyText]="bodyText()"
            [verifyButtonText]="verifyButtonText()"
            [cancelButtonText]="cancelButtonText() ?? i18n('button.cancel')"
            [buttonLoading]="buttonLoading()"
            [displayCancelButton]="displayCancelButton()"
            [disableVerifyButton]="disableVerifyButton()"
            (closeModal)="closeModal('modal close')"
            (verify)="verify.emit()"
            [isDestructiveAction]="isDestructiveAction()"
          />
        }
      </ds-box-content>
    </ds-modal>
  `,
  styleUrl: './verify-choice-modal.wrapper.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerifyChoiceModalWrapper {
  protected readonly i18n = useI18n();

  readonly open = model(false);
  readonly verify = output();
  readonly closeEvent = output();

  readonly headingText = input.required<string>();
  readonly verifyButtonText = input.required<string>();
  readonly cancelButtonText = input<string>();
  readonly buttonLoading = input(false);
  readonly displayCancelButton = input(true, { transform: booleanAttribute });
  readonly disableVerifyButton = input(false, { transform: booleanAttribute });
  readonly isDestructiveAction = input(false, { transform: booleanAttribute });
  readonly bodyText = input<string | TemplateRef<unknown>>();
  readonly size = input<'sm' | 'md' | 'lg'>('sm');

  readonly modal: Signal<ElementRef<HTMLElement>> = viewChild.required('modal', { read: ElementRef });
  protected readonly openOrClosing = modalOpenOrClosing(this.open, this.modal);

  protected closeModal(reason: 'programmatically' | 'modal close' | 'backdrop click' | 'keydown') {
    if (reason === 'programmatically') {
      return;
    }

    this.open.set(false);
    this.closeEvent.emit();
  }
}
